/**
 * ManageList — the protocols agent's entry screen, "Your processes" (the
 * approved Studio redesign, Main artboard, 23 September 2026): every process
 * the signed-in user can see in one place, and the ways to start a new one:
 * "Start modelling" opens the Studio on its canvas (`/studio`, the first
 * step creating the draft — Joël, 2 November 2026: a user can use text AND
 * visual forms of modelling), "Start from scratch" opens it on its editor
 * (`/studio?write`, Joël, 9 October 2026) and files the text as the source
 * when the author continues, and "Start from an existing document" opens
 * the approved-documents picker and the Studio on the same editor with that
 * document's text loaded (`/studio?source=<document id>`). Import stays for
 * the case none covers.
 *
 * Lists one row per protocol — never one per version (architecture.md §1:
 * "the process list groups by process, not by version"): the row carries the
 * protocol's entry version (the one in force, or else its newest) and, beside
 * it, a chip for each of its other open drafts (`include_drafts`), so
 * "Draft v3 · v2 is live" is one row, not two. The pen opens the newest
 * draft; tapping a row opens the ProtocolDetail drawer, which lists every
 * version. The filters and the search narrow that one list in the browser:
 * `GET /protocols` answers free text with 501, and every filter here is a fact
 * the row already carries (status, author).
 *
 * Fluid down to phone width: the filter column becomes a wrapping row of
 * chips, the start panels stack, the toolbar wraps, and each process row
 * stacks its columns.
 *
 * Four async states per frontend/CLAUDE.md. Import is server-authoritative
 * (creates a clinical resource) — no optimistic UI; on success the list is
 * invalidated/refetched.
 *
 * Design: flat hairline rows, state chip uses colour + label (never
 * colour-only), Action Blue pill CTAs, scale-0.95 press, 44px targets.
 */

import { useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, FileText, PenLine, Plus, Search, Upload } from 'lucide-react'

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Skeleton, Textarea, formatDayStamp, toast, useAuthStore } from '@ai4nr/tricorder-ui'
import {
  listProtocolsWithDrafts,
  createProtocol,
  createProtocolVersion,
  fetchProtocolVersion,
  fetchProtocolVersions,
  ApiError,
  type ProtocolSummary,
  type ProtocolsWithDrafts,
} from '@/api/protocols'
import { useNavigate } from 'react-router-dom'
import { STATE_CHIP_CLASS } from './stateChip'
import { ProtocolDetail } from './ProtocolDetail'
import { ApprovedDocuments } from './ApprovedDocuments'

/** The list's filters, each a fact the row carries. */
type ProcessFilter = 'all' | 'myDrafts' | 'inReview' | 'archived'

const FILTERS: ProcessFilter[] = ['all', 'myDrafts', 'inReview', 'archived']

/**
 * One row of the list: a protocol's entry version (the one in force, or else
 * its newest) and its other open drafts, newest first. One row per protocol —
 * a draft never gets its own row.
 */
interface ProcessRow {
  entry: ProtocolSummary
  drafts: ProtocolSummary[]
}

/**
 * The version the pen edits: the newest draft, whichever row slot it sits in.
 * Null when nothing of the protocol is editable (a live version with no
 * draft).
 */
function editableVersionOf(row: ProcessRow): ProtocolSummary | null {
  if (row.drafts.length > 0) return row.drafts[0]
  return row.entry.status === 'draft' ? row.entry : null
}

function matchesFilter(row: ProcessRow, filter: ProcessFilter, userId: string | null): boolean {
  switch (filter) {
    case 'all':
      return true
    // "My drafts" follows the drafts wherever they sit: a draft stacked on a
    // live version keeps that protocol in the filter. Any draft counts, not
    // only the newest (an entry that is itself the draft is included too).
    case 'myDrafts':
      return (
        userId !== null &&
        [row.entry, ...row.drafts].some((v) => v.status === 'draft' && v.created_by === userId)
      )
    // A version lands in review only as the entry (submit publishes at once
    // while no approval connector exists), so the entry alone decides.
    case 'inReview':
      return row.entry.status === 'in_review'
    case 'archived':
      return row.entry.status === 'archived'
  }
}

// The process table's columns, shared by its header and every row so the two
// cannot drift. Below md each row stacks instead.
const ROW_GRID = '@2xl:grid @2xl:grid-cols-[minmax(0,2.4fr)_minmax(0,1.1fr)_minmax(0,0.8fr)_minmax(0,1.3fr)] @2xl:items-center @2xl:gap-4'

export function ManageList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const [selected, setSelected] = useState<ProtocolSummary | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [filter, setFilter] = useState<ProcessFilter>('all')
  const [search, setSearch] = useState('')
  // Starting from an approved document is the secondary start: its list opens on demand.
  const [fromDocument, setFromDocument] = useState(false)

  const {
    data,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery<ProtocolsWithDrafts>({
    queryKey: ['alg', 'protocols', 'all'],
    queryFn: () => listProtocolsWithDrafts(),
  })

  // One row per protocol, its drafts grouped under it. The server sends the
  // drafts in protocol-id order, newest first within a protocol, so grouping
  // by id preserves that order.
  const rows = useMemo<ProcessRow[]>(() => {
    if (!data) return []
    const draftsByProtocol = new Map<string, ProtocolSummary[]>()
    for (const draft of data.drafts) {
      const grouped = draftsByProtocol.get(draft.id)
      if (grouped) grouped.push(draft)
      else draftsByProtocol.set(draft.id, [draft])
    }
    return data.protocols.map((entry) => ({ entry, drafts: draftsByProtocol.get(entry.id) ?? [] }))
  }, [data])

  const counts = useMemo(() => {
    const out: Record<ProcessFilter, number> = { all: 0, myDrafts: 0, inReview: 0, archived: 0 }
    for (const row of rows) {
      for (const f of FILTERS) if (matchesFilter(row, f, userId)) out[f] += 1
    }
    return out
  }, [rows, userId])

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase()
    return rows.filter(
      (row) =>
        matchesFilter(row, filter, userId) &&
        (needle === '' ||
          row.entry.title.toLocaleLowerCase().includes(needle) ||
          row.entry.id.toLocaleLowerCase().includes(needle)),
    )
  }, [rows, filter, userId, search])

  const startFrom = (documentId: string) => navigate(`/studio?source=${encodeURIComponent(documentId)}`)

  return (
    <div className="@container">
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 @xl:px-6 @4xl:flex-row @4xl:gap-8 @4xl:py-8">
      <nav aria-label={t('alg.manage.filters.label', 'Filter processes')} className="@4xl:w-56 @4xl:shrink-0">
        <ul className="flex flex-wrap gap-2 @4xl:flex-col @4xl:gap-1">
          {FILTERS.map((f) => (
            <li key={f}>
              <button
                type="button"
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                data-testid={`alg-filter-${f}`}
                className="flex min-h-11 w-full items-center justify-between gap-3 rounded-full border border-[var(--color-surface-border)] px-4 text-[15px] text-[var(--color-surface-ink)] transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] aria-pressed:border-transparent aria-pressed:bg-[var(--color-primary-500)]/10 aria-pressed:font-semibold aria-pressed:text-[var(--color-primary-600)] @4xl:rounded-[10px] @4xl:border-transparent"
              >
                <span>{t(`alg.manage.filters.${f}`)}</span>
                <span className="text-[var(--color-surface-mute)]">{rows.length > 0 ? counts[f] : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="min-w-0 text-[28px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)] @xl:text-[34px]">
              {t('alg.manage.title', 'Your processes')}
            </h1>
            {/* The three starts: modelling first lands on the canvas
                (`/studio`, its first step creating the draft — Joël, 2
                November 2026: a user can use text AND visual forms of
                modelling); from scratch lands on the Write step's empty
                editor (Joël, 9 October 2026); from an existing document
                opens the approved-documents picker and lands there with the
                document's text loaded. */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="pill"
                variant="outline"
                onClick={() => setFromDocument((o) => !o)}
                aria-expanded={fromDocument}
                data-testid="alg-from-document-toggle"
                className="shrink-0"
              >
                <FileText className="h-4 w-4" />
                {t('alg.manage.startFromDocument', 'Start from an existing document')}
              </Button>
              <Button size="pill" variant="outline" onClick={() => navigate('/studio')} data-testid="alg-model-open" className="shrink-0">
                <PenLine className="h-4 w-4" />
                {t('alg.manage.startModelling', 'Start modelling')}
              </Button>
              <Button size="pill" onClick={() => navigate('/studio?write')} data-testid="alg-write-open" className="shrink-0">
                <Plus className="h-4 w-4" />
                {t('alg.manage.startFromScratch', 'Start from scratch')}
              </Button>
            </div>
          </div>
          <label className="relative w-full @xl:w-80">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-surface-mute)]"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('alg.manage.searchProcesses', 'Search processes')}
              aria-label={t('alg.manage.searchProcesses', 'Search processes')}
              className="h-11 w-full rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] pl-10 pr-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]"
              data-testid="alg-process-search"
            />
          </label>
        </div>

        {fromDocument && (
          <section aria-label={t('alg.manage.fromDocument', 'From an approved document')} className="flex flex-col gap-2" data-testid="alg-from-document">
            <ApprovedDocuments onStart={startFrom} />
          </section>
        )}

        <section aria-labelledby="alg-process-list" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="alg-process-list" className="text-[19px] font-semibold text-[var(--color-surface-ink)]">
              {t(`alg.manage.filters.${filter}`)}
            </h2>
            <Button size="pill" variant="outline" onClick={() => setImportOpen(true)} data-testid="alg-import-open">
              <Upload className="h-4 w-4" />
              {t('alg.manage.import', 'Import protocol')}
            </Button>
          </div>

          {isError && !data ? (
            <div
              role="alert"
              className="flex flex-col items-center gap-4 rounded-[18px] border border-[var(--color-surface-border)] px-6 py-12 text-center"
            >
              <AlertCircle className="h-8 w-8 text-[var(--color-tone-bad)]" aria-hidden="true" />
              <p className="text-[17px] text-[var(--color-surface-ink)]">
                {t('alg.manage.loadFailed', 'Could not load protocols.')}
              </p>
              <Button size="pill" onClick={() => void refetch()}>
                {t('alg.run.retry', 'Try again')}
              </Button>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[60px] w-full rounded-[18px]" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <p className="rounded-[18px] border border-[var(--color-surface-border)] px-6 py-12 text-center text-[17px] text-[var(--color-surface-mute)]">
              {rows.length === 0
                ? t('alg.manage.empty', 'No processes yet')
                : t('alg.manage.noMatch', 'No processes match.')}
            </p>
          ) : (
            <div className="overflow-hidden rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)]">
              <div
                aria-hidden="true"
                className={`hidden border-b border-[var(--color-surface-border)] py-3 pl-5 pr-[4.25rem] text-[13px] font-semibold text-[var(--color-surface-mute)] ${ROW_GRID}`}
              >
                <span>{t('alg.manage.columns.process', 'Process')}</span>
                <span>{t('alg.manage.columns.status', 'Status')}</span>
                <span>{t('alg.manage.columns.version', 'Version')}</span>
                <span>{t('alg.manage.columns.lastChange', 'Last change')}</span>
              </div>
              <ul aria-busy={isFetching} data-testid="alg-manage-list">
                {visible.map((row) => (
                  <ManageRow
                    key={row.entry.id}
                    row={row}
                    onOpen={() => setSelected(row.entry)}
                    onEdit={() => {
                      const editable = editableVersionOf(row)
                      if (editable) navigate(`/studio/${editable.id}/${editable.version}`)
                    }}
                  />
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>

      <ProtocolDetail
        protocolId={selected?.id ?? null}
        protocolTitle={selected?.title ?? ''}
        open={selected !== null}
        onOpenChange={(open) => { if (!open) setSelected(null) }}
      />

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
    </div>
  )
}

function ManageRow({
  row,
  onOpen,
  onEdit,
}: {
  row: ProcessRow
  onOpen: () => void
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const { entry, drafts } = row
  const statusLabel = t(`alg.manage.status.${entry.status}`)
  const editable = editableVersionOf(row)
  // The row's "last change" is the protocol's latest move, wherever it
  // happened: the entry's or any draft's.
  const lastChange = [entry, ...drafts].reduce((latest, v) =>
    Date.parse(v.updated_at) > Date.parse(latest.updated_at) ? v : latest,
  )

  return (
    <li className="flex items-stretch border-b border-[var(--color-surface-border)] last:border-b-0">
      <button
        type="button"
        onClick={onOpen}
        className={`flex min-h-11 min-w-0 flex-1 flex-col gap-1.5 py-3.5 pl-5 pr-3 text-left transition active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-500)] ${ROW_GRID}`}
        data-testid="alg-manage-row"
      >
        <span className="min-w-0">
          <span className="block truncate text-[16px] font-semibold text-[var(--color-surface-ink)]">
            {entry.title}
          </span>
          {entry.description && (
            <span className="block truncate text-[13px] text-[var(--color-surface-mute)]">{entry.description}</span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 @2xl:contents">
          {/* The entry's chip, then a chip per open draft stacked on it —
              "Draft v3 · v2 is live" as one row. */}
          <span className={`w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${STATE_CHIP_CLASS[entry.status]}`}>
            {statusLabel}
          </span>
          {drafts.map((draft) => (
            <span
              key={draft.version}
              className={`w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${STATE_CHIP_CLASS[draft.status]}`}
              data-testid="alg-manage-draft"
            >
              {t('alg.manage.openDraft', 'Draft {{version}}', { version: draft.version })}
            </span>
          ))}
          <span className="text-[14px] text-[var(--color-surface-ink)]">{entry.version}</span>
          <span className="text-[14px] text-[var(--color-surface-mute)]">
            {formatDayStamp(lastChange.updated_at, t)}
          </span>
        </span>
      </button>
      {/* A fixed-width slot on every row keeps the columns aligned with the
          header whether or not the row has an editable draft. */}
      <span className="flex w-14 shrink-0 items-center justify-center">
        {editable && (
          <button
            type="button"
            onClick={onEdit}
            className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-primary-600)] transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
            data-testid="alg-studio-edit"
            aria-label={t('alg.manage.editInStudio', 'Edit in Studio')}
            title={t('alg.manage.editInStudio', 'Edit in Studio')}
          >
            <PenLine className="h-4 w-4" />
          </button>
        )}
      </span>
    </li>
  )
}

// ── Import dialog ────────────────────────────────────────────────────────────

function ImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [asVersion, setAsVersion] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)

  const importMutation = useMutation({
    mutationFn: async (spec: unknown) => {
      // The spec names the protocol: a new one is created under its id, which
      // is also its title until the author renames it (a spec has no title), and a
      // new version continues the existing protocol's newest version with the
      // imported spec, keeping that version's title and settings.
      const id = extractProtocolId(spec)
      if (!id) throw new ApiError('INVALID_SPEC', t('alg.manage.importInvalidJson', 'Invalid JSON. Please check the spec.'), 400)
      if (!asVersion) return createProtocol(id, id, spec)
      const [newest] = await fetchProtocolVersions(id)
      const base = await fetchProtocolVersion(id, newest.version)
      return createProtocolVersion(base, spec)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alg', 'protocols'] })
      onOpenChange(false)
      setText('')
      setAsVersion(false)
      setParseError(null)
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('alg.manage.importFailed', 'Import failed.')
      toast({ variant: 'destructive', description: message })
    },
  })

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    void file.text().then((content) => setText(content))
  }

  function handleSubmit() {
    setParseError(null)
    let spec: unknown
    try {
      spec = JSON.parse(text)
    } catch {
      setParseError(t('alg.manage.importInvalidJson', 'Invalid JSON. Please check the spec.'))
      return
    }
    importMutation.mutate(spec)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] @xl:max-w-lg" data-testid="alg-import-dialog">
        <DialogHeader>
          <DialogTitle>{t('alg.manage.importTitle', 'Import protocol spec')}</DialogTitle>
          <DialogDescription>
            {t('alg.manage.importDescription', 'Paste a protocol spec as JSON or upload a file.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('alg.manage.importPlaceholder', 'Paste protocol JSON …')}
            className="min-h-[160px] font-mono text-[13px]"
            data-testid="alg-import-textarea"
          />

          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-[14px] text-[var(--color-primary-600)]">
            <Upload className="h-4 w-4" />
            <span className="underline-offset-2 hover:underline">
              {t('alg.manage.import', 'Import protocol')}
            </span>
            <Input
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={handleFile}
              data-testid="alg-import-file"
            />
          </label>

          <label className="flex items-center gap-2 text-[14px] text-[var(--color-surface-ink)]">
            <input
              type="checkbox"
              checked={asVersion}
              onChange={(e) => setAsVersion(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-surface-border)] text-[var(--color-primary-500)] focus:ring-2 focus:ring-[var(--color-primary-400)]"
              data-testid="alg-import-as-version"
            />
            {t('alg.manage.importNewVersion', 'Import as a new version of an existing protocol')}
          </label>

          {parseError && (
            <p role="alert" className="text-[14px] text-[var(--color-tone-bad)]">
              {parseError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            size="pill"
            disabled={text.trim() === '' || importMutation.isPending}
            onClick={handleSubmit}
            data-testid="alg-import-submit"
          >
            {t('alg.manage.importSubmit', 'Import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// extractProtocolId pulls the protocol id from a parsed spec. Tolerant of the two shapes seen in authoring specs:
// a top-level `id` or a nested `protocol.id`. Returns null when neither is a
// non-empty string (the caller then surfaces an invalid-spec error).
function extractProtocolId(spec: unknown): string | null {
  if (typeof spec !== 'object' || spec === null) return null
  const obj = spec as Record<string, unknown>
  if (typeof obj.id === 'string' && obj.id !== '') return obj.id
  const nested = obj.protocol
  if (typeof nested === 'object' && nested !== null) {
    const nid = (nested as Record<string, unknown>).id
    if (typeof nid === 'string' && nid !== '') return nid
  }
  return null
}
